/**
 * Utility functions for Catalog module
 */
import type { CustomAttribute, RelatedProductItem, AutoRelatedProduct, ProductReviewItem } from '../types'

/**
 * Find a custom attribute value by attribute code
 */
export function findCustomAttributeValue(
  customAttributes: CustomAttribute[] | undefined | null,
  attributeCode: string
): string | null {
  if (!customAttributes) return null
  const attribute = customAttributes.find(attr => attr.attribute_code === attributeCode)
  return attribute ? attribute.value : null
}

/**
 * Filter products that are in stock
 */
export function filterInStockProducts<T extends { custom_attributes?: CustomAttribute[] }>(
  products: T[] | undefined | null
): T[] {
  if (!products) return []
  return products.filter(product => {
    const isInStock = findCustomAttributeValue(product.custom_attributes, 'is_in_stock')
    return isInStock !== 'Out of Stock'
  })
}

/**
 * Slice products based on device breakpoint
 */
export function sliceProductsByDevice<T extends RelatedProductItem | AutoRelatedProduct>(
  products: T[],
  isMobile: boolean,
  isTablet: boolean
): T[] {
  if (isMobile || isTablet) {
    return products.slice(0, 4)
  }
  return products.slice(0, 5)
}

/**
 * Check if a promotional period is currently active
 */
export function isPromoActive(fromDate?: string, toDate?: string): boolean {
  if (!fromDate || !toDate) return false
  const now = new Date()
  return new Date(toDate) >= now && new Date(fromDate) <= now
}

/**
 * Preprocess reviews: normalize dates and provide default ratings
 */
export function preprocessReviews(reviews: ProductReviewItem[]): ProductReviewItem[] {
  return reviews.map((review) => {
    const dateOnly = review.created_at.split(' ')[0]
    const defaultRating = { name: 'default_code', value: '5' }

    return {
      summary: review.summary,
      text: review.text,
      nickname: review.nickname,
      created_at: dateOnly,
      average_rating: review.ratings_breakdown.length > 0 ? review.average_rating : 5,
      ratings_breakdown: review.ratings_breakdown.length > 0 ? review.ratings_breakdown : [defaultRating],
    }
  })
}

